/**
 * Audit trail.
 *
 * Read-only by construction: the trail is append-only at the database-grant
 * level and there is no write, update or delete operation for it anywhere in the
 * contract. That is what turns "a record of which user created and modified each
 * DeCA" from an application convention into an enforced property, and it is why
 * this screen offers filters and nothing else.
 */

import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { AuditEvent } from '@deca/api'

import { useAuditEvents } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { readsAcrossOrganisations } from '../deca/reach.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime } from '../deca/format.ts'
import { AdminShell } from './AdminShell.tsx'
import { Card, NoAccessState } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { PAGE_DEFAULT, pagedSearchSchema, PER_PAGE } from './searchSchemas.ts'

/**
 * The two values of an event that records a change: a corrected NIF (`organisation.nif.change`), a
 * suspension or a reactivation carry `metadata: { from, to }`. They were in the answer and not on
 * the screen, so the row said that a company's NIF had been changed and not from what to what
 * (2026-10-13). Read by shape, not by action: whichever event keeps both values shows them.
 */
function valueChangeOf(event: AuditEvent): { from: string; to: string } | null {
  const { from, to } = (event.metadata ?? {}) as { from?: unknown; to?: unknown }
  return typeof from === 'string' && typeof to === 'string' ? { from, to } : null
}

export function AdminAuditScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const { organisation: context } = useOrganisationChoice()
  const across = readsAcrossOrganisations(abilities, context)

  const search = pagedSearchSchema.parse(useSearch({ strict: false }))
  const list = useAuditEvents(
    {
      page: search.page ?? PAGE_DEFAULT,
      perPage: PER_PAGE,
      // #61.1. The tab looked near-empty because every administrative act on a client is filed
      // against **the client** (`admin.ts`, deliberate and pinned by `admin-crud.test.ts`), so
      // ASGTRANS's own trail held only what it had done to itself. Widening the *read* shows the
      // work; nothing about where events are filed moves, and it could not — the trail is
      // append-only in the database grants.
      ...(across ? { allOrganisations: true } : {}),
      // Written by the box in the top bar, which on this tab searches the trail by action
      // (2026-10-13); the card had a field of its own for it until then.
      ...(search.q ? { action: search.q } : {}),
    },
    abilities.administer,
  )
  // A page past the end shows the last page, not «Sin resultados» (#165).
  useStepBackPastEnd(list.data?.meta, search.page ?? PAGE_DEFAULT, (page) => {
    void navigate({ to: '/admin/auditoria', search: { ...search, page }, replace: true })
  })

  if (!abilities.administer) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  const columns: Column<AuditEvent>[] = [
    {
      key: 'at',
      header: t('admin.audit.at'),
      numeric: true,
      render: (row) => formatDateTime(row.at, i18n.language),
      width: '180px',
    },
    {
      key: 'action',
      header: t('admin.audit.action'),
      // A phone card's heading reads «when · what» (#111); who, on what and from where are its fields.
      card: 'title',
      render: (row) => <code className="mono">{row.action}</code>,
    },
    // Only across companies: within one, every entry is that company's and the column says the
    // same thing on every row.
    ...(across
      ? [
          {
            key: 'organisation',
            header: t('admin.audit.organisation'),
            render: (row: AuditEvent) => (
              <span className="truncate" style={{ maxWidth: 170, display: 'block' }}>
                {row.organisationName}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'actor',
      header: t('admin.audit.actor'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span>{row.actor.display}</span>
          <span className="small muted">{row.actor.type}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      header: t('admin.audit.subject'),
      render: (row) => {
        const change = valueChangeOf(row)
        if (!row.subjectId && !change) return null
        return (
          <div className="stack-sm" style={{ gap: 0 }}>
            {row.subjectId ? (
              <span className="mono small truncate" style={{ display: 'block', maxWidth: 260 }}>
                {row.subjectType}: {row.subjectId}
              </span>
            ) : null}
            {change ? (
              <span className="small truncate" style={{ display: 'block', maxWidth: 260 }}>
                {`${change.from} → ${change.to}`}
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      key: 'ip',
      header: t('admin.audit.ip'),
      numeric: true,
      render: (row) => row.ip,
    },
  ]

  return (
    <AdminShell heading={t('admin.audit.title')} subtitle={t('admin.audit.subtitle')}>
      <Card flush>
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('admin.audit.title')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />

        {list.data?.meta ? (
          <Pagination
            page={list.data.meta.page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            totalPages={list.data.meta.totalPages}
            onPage={(page) =>
              void navigate({ to: '/admin/auditoria', search: { page, q: search.q } })
            }
          />
        ) : null}
      </Card>
    </AdminShell>
  )
}
